import React from 'react';
import type {ArrayValues, HasReadonlyKeys, LiteralUnion} from 'type-fest';

import type {
  HTMLAttributes,
  SpacingNumber,
  SpringTooling,
} from '../__SharedTypes';
import type {TooltipProps} from '../Tooltip';
import type {ButtonProps} from '../Button';
import type {CanBeReadonly} from '../../utils';
import type {TruncationProps} from '../../hooks';

export type ChangeType =
  | 'changeStart'
  | 'change'
  | 'changeEnd'
  | 'changeCancel';

type SelectOptionExtendedProps = {
  /**
   * Visible text for the option
   */
  label: string | React.ReactNode;
  /**
   * Icon prefix to the left of the option's label text
   */
  icon?: any;
  /**
   * Descriptive text that sits below the entire set of options in the dropdown; often used in Designer
   */
  note?: string;
  /**
   * Descriptive text that sits directly below each option's label text
   */
  description?: string;
  /**
   * Suffix content to the right of the option's label text
   */
  suffix?: React.ReactNode;
  /**
   * Disables the option (`isDisabled` is the preferred property to use, but we support both for parity)
   */
  disabled?: boolean;
  /**
   * Disables the option
   */
  isDisabled?: boolean;
  /**
   * Used for turning an option into an action button rather than an actual selectable choice (not recommended)
   */
  customClickAction?: () => void;
  /**
   * Custom metadata about the option if you need to store additional information to reference elsewhere
   */
  meta?: object;
  /**
   * Controls if and how a tooltip renders for the option
   */
  tooltipProps?: CanBeReadonly<Omit<TooltipProps, 'children'>>;
};

export type SelectOption = CanBeReadonly<
  SelectOptionExtendedProps & {
    shouldHideLabel?: never;
    /**
     * The actual value stored
     */
    value: string;
    options?: never;
  }
>;
export type SelectOptionGroup = CanBeReadonly<
  SelectOptionExtendedProps & {
    shouldHideLabel?: boolean;
    value?: never;
    options: CanBeReadonly<Array<SelectOption>>;
  }
>;

/**
 * We are creating a union with `ReadOnly` here so users can define their `option` objects either inline or as a declared constant var.
 *
 * Although the following is immutable (i.e. `ReadOnly`), typescript will allow it to pass to a mutable type.
 * ```
 * <Select option={[{label: 'foo', value: 'bar'}] as const} />
 * ```
 *
 * The following will create an immutable type, but typescript will not allow it to pass to a mutable type.
 * ```
 * const option = {label: 'foo', value: 'bar'} as const;
 * <Select option={[option]} />
 * ```
 *
 * By creating a union of the two for `SelectOption` and `SelectOptionGroup`, we can allow users to define their `option` objects either inline or as a declared constant var.
 */
export type SelectOptions = CanBeReadonly<
  Array<SelectOption | SelectOptionGroup>
>;

export type SelectProps<TOptions extends SelectOptions = []> = {
  /**
   * The color of the Select trigger button.
   * @default 'default'
   */
  colorVariant?: Exclude<
    ButtonProps['colorVariant'],
    'primary' | 'danger' | 'success'
  >;
  /**
   * The initial open state of the Select. Has no effect when Select is controlled (when the `isOpen` prop is used).
   */
  defaultIsOpen?: boolean;
  /**
   * Text content for a note that is shown when no options are currently active or when the active option has no associated note text.
   */
  defaultNoteText?: string;
  /**
   * Number value for width of the dropdown.
   * When this prop is not specified, the width of the dropdown will match the width of the trigger button (if `shouldDropdownMatchTriggerWidth` is `true`)
   * or adapt to the width of the content (if `shouldDropdownMatchTriggerWidth` is `false`).
   * This prop is useful when you want the dropdown width to be a specific fixed size.
   */
  dropdownWidth?: number;
  /**
   * The space between the trigger button and the dropdown, as a spacing
   * number (multiples of 4px). Defaults to a 4px gap.
   */
  gutter?: SpacingNumber;
  /**
   * Controls whether the dropdown width should match the trigger button width.
   * When `true`, the dropdown will be the same width as the trigger button.
   * When `false`, the dropdown will adapt to its content width.
   * Has no effect when `dropdownWidth` is specified.
   * @default true
   */
  shouldDropdownMatchTriggerWidth?: boolean;
  /**
   * The id of the component.
   */
  id?: string;
  /**
   * Applies branded font styling (WF Visual Sans) to the trigger label and dropdown options.
   *
   * Fixed/non-scaling: does not respond to `SizeContext`.
   * @default false
   */
  isBranded?: boolean;
  /**
   * Disables the control.
   */
  isDisabled?: boolean;
  /**
   * Renders the error state.
   */
  isError?: boolean;
  /**
   * Renders the loading state.
   */
  isLoading?: boolean;
  /**
   * The controlled open state for the Select.
   * Combine this with `onRequestOpen` and `onRequestClose`.
   */
  isOpen?: boolean;
  /**
   * Callback when the Select value has changed.
   */
  onChange?: (
    newValue: HasReadonlyKeys<TOptions> extends true
      ? ExtractOptionValues<ArrayValues<TOptions>>
      : LiteralUnion<ExtractOptionValues<ArrayValues<TOptions>>, string>,
    meta: {
      option: ExtractOptions<ArrayValues<TOptions>>;
      changeType: ChangeType;
    }
  ) => void;
  /**
   * Callback when an option is hovered/focused/active, but without the Select value being changed.
   */
  onPreselect?: (
    newValue: HasReadonlyKeys<TOptions> extends true
      ? ExtractOptionValues<ArrayValues<TOptions>>
      : LiteralUnion<ExtractOptionValues<ArrayValues<TOptions>>, string>,
    meta: {
      option: ExtractOptions<ArrayValues<TOptions>>;
      changeType: ChangeType;
    }
  ) => void;
  /**
   * Callback when a closeable action occurs:
   * 1. Escape keydown (when the Select dropdown is open).
   * 2. Click outside (when the Select dropdown is open).
   * 3. Click on Select dropdown option.
   *
   * Combine this with `isOpen` and `onRequestOpen` to control the open state.
   */
  onRequestClose?: (event?: Event | React.SyntheticEvent) => void;
  /**
   * Callback when an open action occurs:
   * 1. Click on Select trigger button (when Select dropdown is closed).
   *
   * Combine this with `isOpen` and `onRequestClose` to control the open state.
   */
  onRequestOpen?: (event?: Event | React.SyntheticEvent) => void;
  /**
   * A collection of options for the select.
   */
  options: TOptions | [];
  /**
   * Props for truncating option labels and descriptions.
   */
  optionTruncationProps?: {
    label?: TruncationProps;
    description?: TruncationProps;
    maxWidth?: number;
  };
  /**
   * Text to display when no value is selected.
   */
  placeholder?: string;
  /**
   * Replaces the trigger button label with your own custom React component.
   */
  renderTriggerButtonLabel?: (
    selectedOption?: string | SelectOption | null
  ) => React.ReactNode;
  /**
   * Allows keyboard navigation to land on disabled options in the dropdown when `true`. Skips over disabled options when `false`.
   * @default false
   */
  shouldNavigateDisabledOptions?: boolean;
  /**
   * Positions the select menu on top of the trigger button when `true`. Positions the select menu above or below the trigger button when `false`.
   * @default false
   */
  shouldPositionInline?: boolean;
  /**
   * Truncates the label text in the trigger button when `true`.
   * @default false
   */
  shouldTruncateButtonText?: boolean;
  /**
   * Controls the trigger chevron:
   * - `true` — always hidden
   * - `false` — always shown (ghost selects still hide it when disabled)
   * - omitted — ghost hides at rest, reveals on hover/focus/open; other variants always show
   */
  shouldHideChevron?: boolean;
  /**
   * Internal prop for controlling the amount of left padding on the trigger button label text. Should only be used in Designer in the medium size context. Adds more padding to match our other legacy Selects in Designer when `true`.
   * @default false
   */
  _shouldUseLegacyAlignment?: boolean;
  /**
   * The value of the select.
   */
  value?: string | null;
  /**
   * The variant of the Select trigger button.
   * @default 'solid'
   */
  variant?: ButtonProps['variant'];
  /**
   * Whether to vertically align option content to the top or center.
   * @default 'top'
   */
  verticalOptionAlignment?: 'top' | 'center';
} & Omit<HTMLAttributes, 'onChange'> &
  SpringTooling;

export type PreviewSelectProps<TOptions extends SelectOptions = []> = {
  /**
   * Callback when the Select value has changed.
   */
  onChange: (
    newValue: HasReadonlyKeys<TOptions> extends true
      ? ExtractOptionValues<ArrayValues<TOptions>>
      : LiteralUnion<ExtractOptionValues<ArrayValues<TOptions>>, string>,
    meta: {
      option: ExtractOptions<ArrayValues<TOptions>>;
      changeType: ChangeType;
    }
  ) => void;
} & Omit<SelectProps<TOptions>, 'onChange'>;

/**
 * @TODO
 *
 * Figure out how to make this generic for BaseOption & BaseOptionGroup.
 * It also needs to work with extended types (e.g. SelectOption & SelectOptionGroup).
 *
 * This MIGHT not be possible, but I'm guessing that it is through wizardry
 */
export const isOptionGroup = (
  option: SelectOption | SelectOptionGroup
): option is SelectOptionGroup => 'options' in option;

/**
 * Utility Types
 *
 * These are duplicates of those found in `useSelectionMode`.
 * The reason for this is that Select accepts a `ReactNode` for `label` currently, causing a type mismatch.
 *
 * There is probably a way around that, but it's beyond the scope of this PR.
 */

/**
 * Takes a `BaseOption | BaseOptionGroup` type and returns a union of the `value` props of all options (including those nested in groups).
 */
type ExtractOptionValues<TOption> = TOption extends SelectOption
  ? TOption['value']
  : TOption extends SelectOptionGroup
    ? TOption['options'][number]['value']
    : never;

/**
 * Takes a `BaseOptions` type and returns a union of all options (including those nested in groups).
 */
type ExtractOptions<TOption> = TOption extends SelectOption
  ? TOption
  : TOption extends SelectOptionGroup
    ? TOption['options'][number]
    : never;
